import * as yup from 'yup'
import { phoneValidationYup } from '../../FormFields/PhoneNumberInput/PhoneNumberInputField'
import { areaUnits, regulatoryCategories, regulatoryTypes, recordTypes, bathroomTypes, bathroomAmenitySlugs, bathroomAmenitySubTypes, bedSizes, bedTypes } from './PropertyFormConstants'
import { hasLinkedService } from '../../../Helpers/Property/hasLinkedService'

export const blankProperty = () => {
  return {
    created: new Date(),
    buildingId: '',
    title: '',
    type: '',
    hideExactCoordinates: false,
    description: '',
    aliases: [],
    owner: {
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
    },
    registrationInfo: {
      regulatoryCategory: '',
      regulatoryType: '',
      regulatorySubtype: '',
      records: []
    },
    coordinates: [],
    address: {
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: null,
      country: ''
    },
    featuredImage: '',
    thumbnailImage: '',
    imageGallery: [],
    calendars: [],
    listed: [],
    about: {
      summary: '',
      sections: [],
      amenities: [], // needs mega checkbox mechanism
      guests: 0,
      guestInfo: {
        adults: 0,
        children: 0,
        infants: 0,
        pets: 0,
      },
      bedrooms: 0,
      beds: 0,
      otherBeds: 0,
      bathrooms: 0,
      baths: 0,
      halfBathrooms: 0,

      checkInHour: 16,
      checkInMinute: 0,
      checkOutHour: 11,
      checkOutMinute: 0,
      guestInstructions: [],
      guestCheckOutInstructions: [],

    },
    multiUnit: {
      isMultiUnit: false,
      linkedUnits: [],
    },
    pricing: {
      baseRate: 0,
      deposit: 0,
      cleaning: 0,
      basicCleaning: 0,
      tax: 0,
      extraFees: [],
    },
    // TODO: ADD PRICING OBJECT
    virtualTour: '',
    // add custom validator to make sure at least one person is on the list
    authorizedUsers: [], // fill on submit - or server side - validate or send email
    public: true,
    services: {
      cleaning: false,
      bookingGuestInfo: false,
      bookingGuestArrivalTime: false,
      bookingGuestId: false,
      bookingGuestEmail: '',
      autoSendBookingGuestInfo: false,
    },
    linkedServices: [],
    spaces: {
      bathrooms: [],
      bedrooms: [],
      livingRooms: [],
      offices: [],
      diningRooms: [],
    },
    platformSettings: {
      vrboIntegration: {
        quoteHold: false
      }
    },
  }
}

export const propertySchema = yup.object().shape({
  title: yup.string().required(),
  buildingId: yup.string(),
  type: yup.string(),
  description: yup.string().required(),
  aliases: yup.array().of(yup.object().shape({
    alias: yup.string().required(),
    aliasSource: yup.string().required(),
  })),
  owner: yup.object({
    firstName: yup.string(),
    lastName: yup.string(),
    phone: phoneValidationYup('phone'),
    email: yup.string().email()
  }),
  registrationInfo: yup.object({
    regulatoryCategory: yup.string().oneOf(regulatoryCategories.map(option => option.value)),//.required(),
    regulatoryType: yup.string().oneOf(regulatoryTypes.map(option => option.value)),//.required(),
    regulatorySubtype: yup.string(),
    records: yup.array().of(yup.object().shape({
      recordType: yup.string().oneOf(recordTypes.map(option => option.value)).required(),
      value: yup.string().required(),
      expiration: yup.date().nullable()
    }))
  }),
  address: yup.object({
    address1: yup.string().required(),
    address2: yup.string(),
    city: yup.string().required(),
    state: yup.string().required(),
    // zip: yup.string().required(),
    zip: yup.number().required(),
    country: yup.string().required(),
  }),
  area: yup.object({
    value: yup.number(),//.required(),
    // only make unit required if area.value is provided
    unit: yup.string().oneOf(areaUnits.map(option => option.value)).when('value', {
      is: (value: any) => value !== undefined && value !== null && value !== '',
      then: yup.string().oneOf(areaUnits.map(option => option.value)).required(),
      otherwise: yup.string().oneOf(areaUnits.map(option => option.value))
    }),
  }),
  // coordinates: [longitude, latitude] make sure number is between -180 to 180 and -90 to 90
  coordinates: yup.array().of(yup.number().required()).length(2),
  hideExactCoordinates: yup.boolean(),
  featuredImage: yup.string().url(),
  thumbnailImage: yup.string().url(),
  imageGallery: yup.array().of(yup.object().shape({
    url: yup.string().url().required(),
    title: yup.string(),
    description: yup.string(),
    thumbUrl: yup.string().url()
  })),
  calendars: yup.array().of(yup.object().shape({
    source: yup.string().required(),
    url: yup.string().url().required()
  })),
  listed: yup.array().of(yup.object().shape({
    source: yup.string().required(),
    url: yup.string().url().required(),
    internalName: yup.string().required(),
    internalTitle: yup.string().required(),
    internalId: yup.string().nullable(),
  })),
  about: yup.object({
    summary: yup.string(),
    sections: yup.array().of(yup.object().shape({
      sectionId: yup.string().required(),
      sectionTitle: yup.string().required(),
      content: yup.string().required(),
      sectionImage: yup.string().nullable()
    })),
    amenities: yup.array().of(yup.string()), // needs mega checkbox mechanism
    guests: yup.number().required().min(1),
    guestInfo: yup.object({
      adults: yup.number().nullable(),
      children: yup.number().nullable(),
      infants: yup.number().nullable(),
      pets: yup.number().nullable(),
    }),
    bedrooms: yup.number().required(),
    beds: yup.number().required(),
    otherBeds: yup.number(),
    bathrooms: yup.number().required(),
    baths: yup.number().required(),
    halfBathrooms: yup.number(),

    checkInHour: yup.number().required(),
    checkInMinute: yup.number().required(),
    checkOutHour: yup.number().required(),
    checkOutMinute: yup.number().required(),

    guestInstructions: yup.array().of(yup.object().shape({
      sectionId: yup.string().required(),
      sectionTitle: yup.string().required(),
      content: yup.string().required(),
      sectionImage: yup.string().nullable()
    })),

    guestCheckOutInstructions: yup.array().of(yup.object().shape({
      sectionId: yup.string().required(),
      sectionTitle: yup.string().required(),
      content: yup.string().required(),
      sectionImage: yup.string().nullable()
    })),

  }),
  multiUnit: yup.object({
    isMultiUnit: yup.bool(),
    linkedUnits: yup.array().of(yup.object().shape({
      propertyId: yup.string().required(),
      relationship: yup.string().required()
    })).when('isMultiUnit', {
      is: true,
      then: yup.array().min(1).required()
    })
  }),
  pricing: yup.object({
    baseRate: yup.number().required(),
    deposit: yup.number().required(),
    cleaning: yup.number().required(),
    basicCleaning: yup.number().required(),
    tax: yup.number().required(),
    extraFees: yup.array().of(yup.object().shape({
      feeId: yup.string().required(),
      amount: yup.number().required(),
      frequency: yup.string().oneOf(['once', 'nightly']).required(),
      title: yup.string().required(),
      description: yup.string().required(),
    })),
  }),
  spaces: yup.object({
    bathrooms: yup.array().of(yup.object().shape({
      title: yup.string().required(),
      description: yup.string(),
      bathroomType: yup.string().oneOf(bathroomTypes.map(o => o.value)).required(),
      amenities: yup.array().of(yup.object().shape({
        slug: yup.string().oneOf(bathroomAmenitySlugs.map(o => o.value)).required(),
        subType: yup.string().oneOf(bathroomAmenitySubTypes.map(o => o.value)),
      })),
    })),
    bedrooms: yup.array().of(yup.object().shape({
      title: yup.string().required(),
      description: yup.string(),
      beds: yup.array().of(yup.object().shape({
        size: yup.string().oneOf(bedSizes.map(o => o.value)).required(),
        bedType: yup.string().oneOf(bedTypes.map(o => o.value)).required(),
        quantity: yup.number().min(1).required(),
      })),
    })),
    livingRooms: yup.array().of(yup.object().shape({
      title: yup.string().required(),
      description: yup.string(),
      beds: yup.array().of(yup.object().shape({
        size: yup.string().oneOf(bedSizes.map(o => o.value)).required(),
        bedType: yup.string().oneOf(bedTypes.map(o => o.value)).required(),
        quantity: yup.number().min(1).required(),
      })),
    })),
    offices: yup.array().of(yup.object().shape({
      title: yup.string().required(),
      description: yup.string(),
      beds: yup.array().of(yup.object().shape({
        size: yup.string().oneOf(bedSizes.map(o => o.value)).required(),
        bedType: yup.string().oneOf(bedTypes.map(o => o.value)).required(),
        quantity: yup.number().min(1).required(),
      })),
    })),
    diningRooms: yup.array().of(yup.object().shape({
      title: yup.string().required(),
      description: yup.string(),
      capacity: yup.number().min(1).required(),
    })),
  }),
  virtualTour: yup.string().url(),
  public: yup.bool(),
  services: yup.object({
    cleaning: yup.bool(),
    additionalCleaning: yup.bool(),
    bookingGuestInfo: yup.bool(),
    bookingGuestArrivalTime: yup.bool(),
    bookingGuestId: yup.bool(),
    bookingGuestEmail: yup.string(),
    autoSendBookingGuestInfo: yup.bool(),
  }),
  platformSettings: yup.object({
    vrboIntegration: yup.object({
      quoteHold: yup.bool()
    })
  })

})


// BELOW ARE GENERAL HELPERS FOR DETERMINING FORM BEHAVIOR

export const propertyEligibility = (values: any) => {

  const hasFontaineLinkedService = hasLinkedService(values, 'fontaine', false)

  const linkedServices: any = {
    earlyCheckIn: true,
    lateCheckOut: true,
    fontaine: true,
    fontainePortal: hasFontaineLinkedService,
    blockingProperty: hasFontaineLinkedService,
    parking: true,
    remoteLock: true,
    gateCode: true,
  }

  const linkedServiceIndexes: any = {
    earlyCheckIn: -1,
    lateCheckOut: -1,
    fontainePortal: -1,
    blockingProperty: -1,
    parking: -1,
    remoteLock: -1,
    gateCode: -1,
  }

  values?.linkedServices?.forEach((linkedService: any, idx: number) => {
    if (linkedService.linkedService && linkedServices[linkedService.linkedService]) {
      linkedServices[linkedService.linkedService] = false
      linkedServiceIndexes[linkedService.linkedService] = idx
    }
  })

  const extraFees: any = {
    hotelCleaning: true,
    hotelDeposit: true,
    hotelResort: true,
    hotelCheckIn: true,
  }

  values?.pricing?.extraFees?.forEach((extraFee: any) => {
    if (extraFee.feeId && extraFees[extraFee.feeId]) {
      extraFees[extraFee.feeId] = false
    }
  })

  const guestInstructions: any = {
    general: true,
    amenities: true,
    additionalFees: true,
    lateCheckInFee: true,
    lock: true,
    internet: true,
    parking: true,
    luggageStorage: true,
    packageDelivery: true,
  }

  values?.about?.guestInstructions?.forEach((guestInstruction: any) => {
    if (guestInstruction.sectionId && guestInstructions[guestInstruction.sectionId]) {
      guestInstructions[guestInstruction.sectionId] = false
    }
  })

  const guestCheckOutInstructions: any = {
    trash: true,
    turnThingsOff: true,
    lockUp: true,
    returnKeys: true,
    thermoStat: true,
    additionalRequests: true,
  }

  values?.about?.guestCheckOutInstructions?.forEach((guestInstruction: any) => {
    if (guestInstruction.sectionId && guestCheckOutInstructions[guestInstruction.sectionId]) {
      guestCheckOutInstructions[guestInstruction.sectionId] = false
    }
  })

  return {
    linkedServices: linkedServices,
    linkedServiceIndexes: linkedServiceIndexes,
    extraFees: extraFees,
    guestInstructions: guestInstructions,
    guestCheckOutInstructions: guestCheckOutInstructions,
  }

}